import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { debounce, maxBy } from 'lodash';
import { push } from 'redux-first-router';
import { useTheme } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import type {
  MarketCapEntity,
  PEEntity,
} from '../../query/fetchIndustryPerformance';
import { hasMinimumCompanies } from '../../query/fetchIndustryPerformance';
import {
  BarCell,
  BarWrapper,
  ChevronRight,
  Name,
  Value,
  NoResults,
  TableRow,
  LastCell,
} from './styled';
import type { SectorTabType } from './SectorPerformance';
import useWindowSize from '@/hooks/useWindowSize';

interface Props {
  data?: MarketCapEntity[] | PEEntity[] | null;
  tab: SectorTabType;
}

interface BarChartProps {
  widthPercentage: number;
  color: string;
}

const Bar = ({ widthPercentage, color }: BarChartProps) => (
  <svg width="100%" height={20}>
    <rect x="0" y="0" width={`${widthPercentage}%`} height="20" fill={color} />
  </svg>
);

const PADDING = 4;

export const BarChart = ({ data: rawData, tab }: Props) => {
  const data = rawData ?? [];
  const theme = useTheme();
  const { trackEvent } = useTracking();
  const showLinks = data.length > 1;
  const colorChartMapping = {
    Good: theme.color.good,
    Bad: theme.color.bad,
    Neutral: theme.color.brand03,
  };
  const ref = useRef<HTMLSpanElement | null>(null);
  const size = useWindowSize();
  const [barWidth, setBarWidth] = useState<number>(0);

  const debounceSetWidth = debounce((width) => {
    setBarWidth(width);
  }, 100);

  useEffect(() => {
    const { current } = ref;
    if (current) {
      const nextWidth = current.clientWidth;
      if (nextWidth > 0 && nextWidth !== barWidth) {
        debounceSetWidth(nextWidth - PADDING);
      }
    }
    return () => {
      debounceSetWidth.cancel();
    };
  }, [size, ref.current, tab]);

  const max = useMemo(() => {
    if (tab === 'pe') {
      return (
        maxBy<PEEntity>(data as PEEntity[], (a) => Math.abs(a.pe))?.pe || 1
      );
    } else {
      return (
        maxBy<MarketCapEntity>(data as MarketCapEntity[], (a) =>
          a.value ? Math.abs(a.value) : undefined
        )?.value || 1
      );
    }
  }, [data]);
  const goToIndustry = useCallback((url: string, name: string) => {
    trackEvent({
      type: 'track',
      action: 'click',
      subject: 'sector-trends-returns-tab-industry',
      properties: name,
    });
    push(url);
  }, []);

  return (
    <>
      {tab === 'pe'
        ? (data as PEEntity[]).map((a, i) => {
            const disabled = a.current || !hasMinimumCompanies(a);
            return (
              <TableRow
                disabled={disabled}
                data-cy-id="valuation-table-row"
                key={i}
                title={a.name}
                onClick={() =>
                  !a.current && showLinks && hasMinimumCompanies(a)
                    ? goToIndustry(a.canonicalUrl, a.name)
                    : null
                }
                current={a.current}
                isChild={a.isChild}
              >
                <Name>{a.name}</Name>
                <Value>{a.pe === 0 ? 'n/a' : a.peFormatted}</Value>
                <BarCell>
                  <BarWrapper ref={i === 0 ? ref : null}>
                    <Bar
                      widthPercentage={Math.abs(a.pe / max) * 100}
                      color={colorChartMapping.Neutral}
                    />
                  </BarWrapper>
                </BarCell>
                <Value>{a.earningsGrowthFormatted}</Value>
                <LastCell>
                  <ChevronRight
                    opacity={
                      a.current === true ? '0' : disabled ? '0.1' : ' 0.3'
                    }
                  />
                </LastCell>
              </TableRow>
            );
          })
        : (data as MarketCapEntity[]).map((a, i) => {
            const disabled = a.current || !hasMinimumCompanies(a);
            return (
              <TableRow
                key={i}
                data-cy-id="returns-table-row"
                title={a.name}
                disabled={disabled}
                onClick={() =>
                  !a.current && showLinks && hasMinimumCompanies(a)
                    ? goToIndustry(a.canonicalUrl, a.name)
                    : null
                }
                current={a.current}
                isChild={a.isChild}
              >
                <Name>{a.name}</Name>
                <Value>{a.valueFormatted}</Value>
                <BarCell>
                  <BarWrapper>
                    {a.value && (
                      <Bar
                        widthPercentage={Math.abs(a.value / max) * 100}
                        color={colorChartMapping[a.value > 0 ? 'Good' : 'Bad']}
                      />
                    )}
                    <ChevronRight
                      opacity={
                        a.current === true ? '0' : disabled ? '0.1' : '0.3'
                      }
                    />
                  </BarWrapper>
                </BarCell>
              </TableRow>
            );
          })}
      {data.length === 0 && (
        <tr>
          <NoResults colSpan={tab === 'pe' ? 5 : 3} data-cy-id="no-results">
            No data found
          </NoResults>
        </tr>
      )}
    </>
  );
};
